import { EmailIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Container } from 'components/Container';
import { ContainerHead } from 'components/ContainerHead';
import { DataTable } from 'components/DataTable/DataTable';
import { orderTable } from 'components/DataTable/DataTableModels';
import { fetchApi } from 'lib/fetchApi';
import { useApi } from 'lib/useApi';
import { Order, User, UserRoles } from 'models/ApiModels';
import React from 'react';
import { useParams } from 'react-router-dom';

export function UserPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: user, setData: setUserData } = useApi<User>(`/users/${id}`);
  const { data: userOrders } = useApi<Order[]>(`/orders/user/${id}`);
  const [isEditing, setIsEditing] = React.useState(false);
  const [email, setEmail] = React.useState(user?.email ?? '');
  const [originalEmail, setOriginalEmail] = React.useState('');
  const toast = useToast();

  React.useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      setOriginalEmail(user.email);
    }
  }, [user?.email]);
  async function onToggleAdminRole(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    if (!user) return;

    const prevVal = user.roles[UserRoles.PortalAdmin];

    setAdminRole(e.target.checked);

    const path =
      (e.target.checked ? '/users/addrole' : '/users/removerole') +
      `?userId=${user.id}&role=portal:admin`;
    const resp = await fetchApi(path);

    if (!resp.ok) {
      const respData = await resp.json();

      setAdminRole(prevVal);
      toast({
        title: 'Fel',
        description: respData.message,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    }
  }

  function setAdminRole(value: boolean): void {
    if (!user) return;

    setUserData({
      ...user,
      roles: {
        ...user.roles,
        [UserRoles.PortalAdmin]: value,
      },
    });
  }

  async function onToggleSecuritasRole(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    if (!user) return;

    const prevVal = user.roles[UserRoles.SecuritasAdmin];

    setSecuritasRole(e.target.checked);

    const path =
      (e.target.checked ? '/users/addrole' : '/users/removerole') +
      `?userId=${user.id}&role=portal:securitas`;
    const resp = await fetchApi(path);

    if (!resp.ok) {
      const respData = await resp.json();

      setSecuritasRole(prevVal);
      toast({
        title: 'Fel',
        description: respData.message,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    }
  }

  function setSecuritasRole(value: boolean): void {
    if (!user) return;

    setUserData({
      ...user,
      roles: {
        ...user.roles,
        [UserRoles.SecuritasAdmin]: value,
      },
    });
  }

  const updateEmail = async (newEmail: string) => {
    setIsEditing(false);
    if (email !== originalEmail) {
      try {
        const path = `/users/changeemail?userId=${user.id}&email=${newEmail}`;

        const response = await fetchApi(path);

        if (response.ok) {
          toast({
            title: 'Sparat',
            description: 'Email sparat',
            status: 'success',
            position: 'top',
            isClosable: true,
          });
        }

        if (!response.ok) {
          const respData = await response.json();

          toast({
            title: 'Fel',
            description: respData.message,
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }
        // optionally update local user state
      } catch (error) {
        console.error('Error updating email:', error);
        // revert or show error message
      }
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    if (email !== user?.email) {
      updateEmail(email);
    }
  };

  return (
    <>
      <ContainerHead title="Kund" backButton={true}>
        <Box display="flex" flexDirection="column">
          <Text fontSize="lg" fontWeight="bold">
            E-Post
          </Text>
          {isEditing ? (
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              autoFocus
            />
          ) : (
            <Text
              onClick={() => setIsEditing(true)}
              cursor="pointer"
              color={email ? 'black' : 'gray.500'}>
              {originalEmail || 'Ingen email'}
            </Text>
          )}
        </Box>
        <Box></Box>
        <Box></Box>
        <Box></Box>
        <Box color="white">
          <Button
            variant="primary"
            onClick={() =>
              (window.location.href = `mailto:${user?.email}?subject="Från mittlager: Hej!"`)
            }>
            <EmailIcon h={3} w={3} mr={3} />
            Kontakta
          </Button>
        </Box>
      </ContainerHead>

      <Container>
        {/* <Flex direction="row">
          <IconCard
            title="Kontakta kund"
            icon={userIcon}
            desc="Maila"
            link={`mailto:${user?.email}?subject="Från mittlager: Hej!"`}
          />
        </Flex> */}

        <Flex align="center" marginY={10}>
          <Switch
            id="admin-toggle"
            size="lg"
            onChange={onToggleAdminRole}
            isChecked={user?.roles['portal:admin'] || false}
          />
          <FormLabel ml={4} htmlFor="admin-toggle">
            Portaladministratör
          </FormLabel>
        </Flex>
        <Flex align="center" marginY={10}>
          <Switch
            id="securitas-toggle"
            size="lg"
            onChange={onToggleSecuritasRole}
            isChecked={user?.roles['portal:securitas'] || false}
          />
          <FormLabel ml={4} htmlFor="securitas-toggle">
            Securitasadministratör
          </FormLabel>
        </Flex>
        <DataTable
          showSearch={false}
          title="Ordrar"
          tableDef={orderTable}
          data={userOrders}
          searchableFields={['unit.facility.name', 'createdAt']}
        />
      </Container>
    </>
  );
}
